/**
 * Company API functions (using Generated SDK where available)
 * ฟังก์ชัน API สำหรับบริษัท (ใช้ SDK ที่สร้างอัตโนมัติในส่วนที่มี)
 */

import { Api5 } from '@/services/client'
import type {
  Company,
  CompanyProject,
  CompanyDocument,
  ChangeHistoryEntry,
  ActivityLogEntry,
  CreateCompanyInput,
  UpdateCompanyInput,
  CompaniesResponse,
} from './types'

/**
 * Handle SDK error and extract message
 */
function handleError(error: unknown, defaultMessage: string): never {
  if (error && typeof error === 'object' && 'message' in error) {
    throw new Error(String(error.message))
  }
  throw new Error(defaultMessage)
}

/**
 * Get list of companies
 * รับรายการบริษัท
 */
// TODO: Migrate query params once OpenAPI spec defines them (GetCompaniesData.query is `never`)
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export async function getCompanies(_params: {
  search?: string
  status?: string
  page?: number
  pageSize?: number
}): Promise<CompaniesResponse> {
  try {
    const { data } = await Api5.getCompanies({
      throwOnError: true,
    })
    return data as unknown as CompaniesResponse
  } catch (error) {
    handleError(error, 'Failed to fetch companies')
  }
}

/**
 * Get single company by ID
 * รับข้อมูลบริษัทตาม ID
 */
export async function getCompany(id: string): Promise<Company> {
  try {
    const { data } = await Api5.getCompaniesById({
      path: { id },
      throwOnError: true,
    })
    return data as unknown as Company
  } catch (error) {
    handleError(error, 'Failed to fetch company')
  }
}

/**
 * Create new company
 * สร้างบริษัทใหม่
 */
export async function createCompany(input: CreateCompanyInput): Promise<Company> {
  const formData = new FormData()

  // Append all text fields
  const textFields: (keyof Omit<CreateCompanyInput, 'evidenceFile'>)[] = [
    'nameTh',
    'nameEn',
    'taxId',
    'registrationNo',
    'registeredCapital',
    'businessType',
    'directors',
    'address',
    'phone',
    'email',
    'contactPerson',
    'contactPhone',
  ]

  textFields.forEach((field) => {
    const value = input[field]
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (value !== undefined && value !== '') {
      formData.append(field, value)
    }
  })

  // Append evidence file if present
  if (input.evidenceFile) {
    formData.append('evidence', input.evidenceFile)
  }

  try {
    const { data } = await Api5.postCompanies({
      // body not yet in OpenAPI spec — cast until spec is updated
      body: formData as never,
      throwOnError: true,
    })
    return data as unknown as Company
  } catch (error) {
    handleError(error, 'Failed to create company')
  }
}

/**
 * Update company
 * อัปเดตบริษัท
 */
export async function updateCompany(id: string, input: UpdateCompanyInput): Promise<Company> {
  const formData = new FormData()

  // Append only provided fields
  Object.entries(input).forEach(([key, value]: [string, unknown]) => {
    if (value !== undefined && key !== 'evidenceFile' && typeof value === 'string') {
      formData.append(key, value)
    }
  })

  if (input.evidenceFile) {
    formData.append('evidence', input.evidenceFile)
  }

  try {
    const { data } = await Api5.patchCompaniesById({
      path: { id },
      // body not yet in OpenAPI spec — cast until spec is updated
      body: formData as never,
      throwOnError: true,
    })
    return data as unknown as Company
  } catch (error) {
    handleError(error, 'Failed to update company')
  }
}

/**
 * Delete company
 * ลบบริษัท
 */
export async function deleteCompany(id: string): Promise<void> {
  try {
    await Api5.deleteCompaniesById({
      path: { id },
      throwOnError: true,
    })
  } catch (error) {
    handleError(error, 'Failed to delete company')
  }
}

/**
 * Get company project history
 * รับประวัติแฟ้มงานของบริษัท
 */
export async function getCompanyProjects(id: string): Promise<CompanyProject[]> {
  try {
    const { data } = await Api5.getCompaniesByIdProjects({
      path: { id },
      throwOnError: true,
    })
    return data as unknown as CompanyProject[]
  } catch (error) {
    handleError(error, 'Failed to fetch company projects')
  }
}

/**
 * Get company documents
 * รับเอกสารบริษัท
 */
export async function getCompanyDocuments(id: string): Promise<CompanyDocument[]> {
  try {
    const { data } = await Api5.getCompaniesByIdDocuments({
      path: { id },
      throwOnError: true,
    })
    return data as unknown as CompanyDocument[]
  } catch (error) {
    handleError(error, 'Failed to fetch company documents')
  }
}

/**
 * Upload company document
 * อัปโหลดเอกสารบริษัท
 */
export async function uploadCompanyDocument(
  companyId: string,
  file: File,
  source: 'initial' | 'change',
  relatedYear?: string
): Promise<CompanyDocument> {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('source', source)
  if (relatedYear) {
    formData.append('relatedYear', relatedYear)
  }

  try {
    const { data } = await Api5.postCompaniesByIdDocuments({
      path: { id: companyId },
      // body not yet in OpenAPI spec — cast until spec is updated
      body: formData as never,
      throwOnError: true,
    })
    return data as unknown as CompanyDocument
  } catch (error) {
    handleError(error, 'Failed to upload document')
  }
}

/**
 * Delete company document
 * ลบเอกสารบริษัท
 */
export async function deleteCompanyDocument(companyId: string, documentId: string): Promise<void> {
  try {
    await Api5.deleteCompaniesByIdDocumentsByDocumentId({
      path: { id: companyId, documentId },
      throwOnError: true,
    })
  } catch (error) {
    handleError(error, 'Failed to delete document')
  }
}

/**
 * Get change history
 * รับประวัติการเปลี่ยนแปลง
 */
export async function getChangeHistory(id: string): Promise<ChangeHistoryEntry[]> {
  try {
    const { data } = await Api5.getCompaniesByIdHistory({
      path: { id },
      throwOnError: true,
    })
    return data as unknown as ChangeHistoryEntry[]
  } catch (error) {
    handleError(error, 'Failed to fetch change history')
  }
}

/**
 * Get activity log
 * รับบันทึกกิจกรรม
 */
export async function getActivityLog(id: string): Promise<ActivityLogEntry[]> {
  try {
    const { data } = await Api5.getCompaniesByIdActivity({
      path: { id },
      throwOnError: true,
    })
    return data as unknown as ActivityLogEntry[]
  } catch (error) {
    handleError(error, 'Failed to fetch activity log')
  }
}
